import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

import { authServie } from 'src/services'
import { QUERY_KEYS, STORAGE_KEYS, URL_QUERY } from 'src/constants'

export const useAuthentication = () => {
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()

  const localStorageRefreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)
  const localStorageAccessToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)

  useEffect(() => {
    const handleTokenValidation = async () => {
      try {
        if (!localStorageRefreshToken || !localStorageAccessToken) return

        const tokenStatus = await authServie.validateToken(
          localStorageRefreshToken,
          localStorageAccessToken,
        )
        const refreshTokenStatus = tokenStatus?.status

        // If the token status is false, remove the tokens and exit
        if (!refreshTokenStatus) {
          localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN)
          localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
          queryClient.removeQueries([QUERY_KEYS.GET_AUTH_USER])
          queryClient.removeQueries([QUERY_KEYS.GET_ME])
        }
      } catch (error) {
        console.error('Error validating token:', error)
      }
    }

    handleTokenValidation()
  }, [localStorageRefreshToken, localStorageAccessToken])

  const storeSession = (accessToken: string, refreshToken: string) => {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken)
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken)
    queryClient.removeQueries([QUERY_KEYS.GET_AUTH_USER])
    queryClient.removeQueries([QUERY_KEYS.GET_ME])
  }

  // Current flow: the admin app sends us back with a one-time code (?handoff=) that is exchanged
  // server-side for the session, so tokens never appear in this page's URL.
  const handoffCode = searchParams.get(URL_QUERY.HANDOFF)
  useEffect(() => {
    if (!handoffCode) return

    setSearchParams(
      (prev) => {
        prev.delete(URL_QUERY.HANDOFF)
        return prev
      },
      { replace: true },
    )
    ;(async () => {
      const session = await authServie.exchangeHandoff(handoffCode)
      if (session?.accessToken && session?.refreshToken) {
        storeSession(session.accessToken, session.refreshToken)
      }
    })()
  }, [handoffCode])

  // Legacy flow (older admin bundles still append the tokens themselves): keep accepting them for
  // the transition and scrub them from the URL immediately. Remove once all admin-ui deploys are current.
  const accessToken = searchParams.get(URL_QUERY.ACCESS_TOKEN)
  const refreshToken = searchParams.get(URL_QUERY.REFRESH_TOKEN)
  useEffect(() => {
    if (!accessToken || !refreshToken) return

    storeSession(accessToken, refreshToken)

    setSearchParams(
      (prev) => {
        prev.delete(URL_QUERY.ACCESS_TOKEN)
        prev.delete(URL_QUERY.REFRESH_TOKEN)

        return prev
      },
      { replace: true },
    )
  }, [accessToken, refreshToken])
}
